import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Users, TrendingUp, Plus, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function CreatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category: '',
    price_gbp: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.creator_status) {
      navigate('/app');
      toast.error('Creator status required');
      return;
    }
    fetchAnalytics();
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/creator/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching creator analytics:', error);
    }
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title || !newCourse.description || !newCourse.category) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/creator/courses`, newCourse, { withCredentials: true });
      toast.success('Course created successfully!');
      setShowCreateDialog(false);
      setNewCourse({ title: '', description: '', category: '', price_gbp: 0 });
      fetchAnalytics();
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  if (!analytics) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('creator.dashboard')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage your courses and track your impact
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-purple-500 hover:bg-purple-600 text-white gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('creator.createCourse')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 border-l-4 border-purple-500 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('creator.totalCourses')}</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{analytics.total_courses}</p>
              </div>
              <BookOpen className="h-10 w-10 text-purple-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-teal-500 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('creator.totalStudents')}</p>
                <p className="text-3xl font-bold text-teal-600 mt-2">{analytics.total_students}</p>
              </div>
              <Users className="h-10 w-10 text-teal-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-green-500 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('creator.totalRevenue')}</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{analytics.total_revenue_credits}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">credits (100% yours!)</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-yellow-500 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Published</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{analytics.published_courses}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </Card>
        </div>

        {/* My Courses */}
        <Card className="p-6 dark:bg-gray-800">
          <h3 className="text-xl font-bold mb-6 dark:text-white">{t('creator.myCourses')}</h3>
          <div className="space-y-4">
            {analytics.courses.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                {t('creator.noCourses')}
              </p>
            ) : (
              analytics.courses.map((course) => (
                <Card key={course.id} className="p-4 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 dark:text-white">{course.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{course.description}</p>
                      <div className="flex gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span>{course.enrollment_count} students</span>
                        <span>•</span>
                        <span>{course.price_credits} credits</span>
                        <span>•</span>
                        <span className={course.is_published ? 'text-green-600' : 'text-yellow-600'}>
                          {course.is_published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/creator/courses/${course.id}`)}
                      size="sm"
                      variant="outline"
                    >
                      Manage
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Create Course Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Course</DialogTitle>
            <DialogDescription>Create a course to share your knowledge</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Course Title</label>
              <Input
                placeholder="e.g., Python for Beginners"
                value={newCourse.title}
                onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                placeholder="Describe what students will learn..."
                value={newCourse.description}
                onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <Select value={newCourse.category} onValueChange={(val) => setNewCourse({...newCourse, category: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Programming">Programming</SelectItem>
                  <SelectItem value="Design">Design</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="Language">Language</SelectItem>
                  <SelectItem value="Cooking">Cooking</SelectItem>
                  <SelectItem value="Fitness">Fitness</SelectItem>
                  <SelectItem value="Music">Music</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Price (Credits)</label>
              <Input
                type="number"
                min="0"
                placeholder="0 for free"
                value={newCourse.price_credits}
                onChange={(e) => setNewCourse({...newCourse, price_credits: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCourse} disabled={loading} className="bg-purple-500 hover:bg-purple-600">
              {loading ? 'Creating...' : 'Create Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
